import { Dropbox, DropboxAuth } from 'dropbox';
import { getCryptoBox } from '../crypto/box';
import { getDatabase } from '../store/database';

// Bundle format for Dropbox sync
interface SyncBundle {
  meta: {
    ledgerId: string;
    version: number;
    timestamp: number;
    chunkCount: number;
    blobCount: number;
  };
  chunks: string[]; // Base64 encoded encrypted chunks
  blobs: { [hash: string]: string }; // Base64 encoded encrypted blobs
}

// Dropbox sync adapter for encrypted bundle storage
export class DropboxAdapter {
  private static instance: DropboxAdapter;
  private dropbox: Dropbox | null = null;
  private auth: DropboxAuth;
  private isAuthenticated = false;
  
  private constructor() {
    const appKey = (import.meta as any).env?.VITE_DROPBOX_APP_KEY || process.env.REACT_APP_DROPBOX_APP_KEY;
    if (!appKey) {
      throw new Error('VITE_DROPBOX_APP_KEY not found in environment');
    }
    
    this.auth = new DropboxAuth({
      clientId: appKey,
      fetch: fetch
    });
  }

  static getInstance(): DropboxAdapter {
    if (!DropboxAdapter.instance) {
      DropboxAdapter.instance = new DropboxAdapter();
    }
    return DropboxAdapter.instance;
  }

  // Initialize OAuth2 PKCE flow
  async startAuth(): Promise<string> {
    const authUrl = await this.auth.getAuthenticationUrl(
      window.location.origin + '/auth-redirect',
      undefined,
      'code',
      'offline',
      undefined,
      undefined,
      true // PKCE
    );
    
    return authUrl.toString();
  }

  // Complete OAuth2 flow with authorization code
  async completeAuth(code: string): Promise<boolean> {
    try {
      await this.auth.getAccessTokenFromCode(window.location.origin + '/auth-redirect', code);
      
      const accessToken = this.auth.getAccessToken();
      if (accessToken) {
        // Store token securely (encrypted in localStorage)
        const cryptoBox = await getCryptoBox();
        const encryptedToken = await cryptoBox.encryptString(accessToken);
        localStorage.setItem('dropbox_token_enc', encryptedToken);
        
        this.dropbox = new Dropbox({ 
          accessToken,
          fetch: fetch
        });
        
        this.isAuthenticated = true;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Dropbox auth error:', error);
      return false;
    }
  }

  // Load stored authentication
  async loadStoredAuth(): Promise<boolean> {
    try {
      const encryptedToken = localStorage.getItem('dropbox_token_enc');
      if (!encryptedToken) return false;
      
      const cryptoBox = await getCryptoBox();
      const accessToken = await cryptoBox.decryptString(encryptedToken);
      
      this.dropbox = new Dropbox({ 
        accessToken,
        fetch: fetch
      });
      
      // Test the token
      await this.dropbox.usersGetCurrentAccount();
      this.isAuthenticated = true;
      return true;
      
    } catch (error) {
      console.error('Failed to load Dropbox auth:', error);
      this.disconnect();
      return false;
    }
  }

  // Disconnect from Dropbox
  disconnect(): void {
    this.dropbox = null;
    this.isAuthenticated = false;
    localStorage.removeItem('dropbox_token_enc');
  }

  // Check if authenticated
  isConnected(): boolean {
    return this.isAuthenticated && this.dropbox !== null;
  }

  // Get account info
  async getAccountInfo(): Promise<any> {
    if (!this.dropbox) throw new Error('Not authenticated');
    return await this.dropbox.usersGetCurrentAccount();
  }

  // Sync ledger to Dropbox
  async syncLedger(ledgerId: string, householdName: string): Promise<boolean> {
    if (!this.dropbox) throw new Error('Not authenticated with Dropbox');
    
    try {
      const db = getDatabase();
      const cryptoBox = await getCryptoBox();
      
      // Get current state from IndexedDB
      const chunks = await db.getDocumentChunks(ledgerId);
      const metadata = await db.getLedgerMetadata(ledgerId);
      
      if (!metadata) throw new Error('Ledger metadata not found');
      
      // Create sync bundle
      const bundle: SyncBundle = {
        meta: {
          ledgerId,
          version: metadata.version,
          timestamp: Date.now(),
          chunkCount: chunks.length,
          blobCount: 0 // TODO: Add blob count when implemented
        },
        chunks: chunks.map(chunk => this.arrayBufferToBase64(chunk.bytes)),
        blobs: {} // TODO: Add blobs when implemented
      };
      
      const bundlePath = `/Budget/${householdName}/ledger.bundle`;
      
      // Check if remote file exists and get its content for merging
      let remoteBundleExists = false;
      let remoteBundle: SyncBundle | null = null;
      
      try {
        const remoteFile = await this.dropbox.filesDownload({ path: bundlePath });
        const remoteContent = await this.blobToText((remoteFile as any).result.fileBinary);
        remoteBundle = JSON.parse(remoteContent);
        remoteBundleExists = true;
      } catch (error) {
        // File doesn't exist yet, which is fine for first sync
        console.log('No existing remote bundle, creating new one');
      }

      // If remote exists and is newer, we need to merge
      if (remoteBundle && remoteBundle.meta.timestamp > metadata.lastAccessed) {
        console.log('Remote bundle is newer, merging...');
        await this.mergeBundles(ledgerId, remoteBundle);
        
        // Re-read local state after merge
        const mergedChunks = await db.getDocumentChunks(ledgerId);
        bundle.chunks = mergedChunks.map(chunk => this.arrayBufferToBase64(chunk.bytes));
        bundle.meta.chunkCount = mergedChunks.length;
        bundle.meta.timestamp = Date.now();
      }

      // Upload the bundle
      const bundleContent = JSON.stringify(bundle, null, 2);
      const bundleBlob = new Blob([bundleContent], { type: 'application/json' });
      
      await this.dropbox.filesUpload({
        path: bundlePath,
        contents: bundleBlob,
        mode: { '.tag': 'overwrite' },
        autorename: false
      });
      
      console.log(`Synced ledger ${ledgerId} to Dropbox: ${bundlePath}`);
      return true;
      
    } catch (error) {
      console.error('Sync to Dropbox failed:', error);
      return false;
    }
  }

  // Download and merge remote bundle
  async downloadAndMerge(ledgerId: string, householdName: string): Promise<boolean> {
    if (!this.dropbox) throw new Error('Not authenticated with Dropbox');
    
    try {
      const bundlePath = `/Budget/${householdName}/ledger.bundle`;
      
      const remoteFile = await this.dropbox.filesDownload({ path: bundlePath });
      const remoteContent = await this.blobToText((remoteFile as any).result.fileBinary);
      const remoteBundle: SyncBundle = JSON.parse(remoteContent);
      
      await this.mergeBundles(ledgerId, remoteBundle);
      return true;
      
    } catch (error) {
      console.error('Download and merge failed:', error);
      return false;
    }
  }

  // Merge remote bundle with local state
  private async mergeBundles(ledgerId: string, remoteBundle: SyncBundle): Promise<void> {
    const db = getDatabase();
    const cryptoBox = await getCryptoBox();
    
    // Convert remote chunks back to bytes and store
    const remoteChunks = remoteBundle.chunks.map((chunk, index) => ({
      ledgerId,
      seq: index,
      bytes: this.base64ToArrayBuffer(chunk)
    }));
    
    // In a real CRDT implementation, we would merge the Yjs updates
    // For now, we replace local with remote (last-writer-wins)
    // TODO: Implement proper Yjs update merging
    console.warn('Using last-writer-wins merge strategy. TODO: Implement CRDT merging');
    
    await db.storeDocumentChunks(ledgerId, remoteChunks.map(c => c.bytes));
    
    // Store blobs if any
    for (const [hash, blobData] of Object.entries(remoteBundle.blobs)) {
      const blobBytes = this.base64ToArrayBuffer(blobData);
      await db.storeBlob(hash, blobBytes);
    }
  }

  // List available bundles in Dropbox
  async listAvailableBundles(): Promise<Array<{ name: string; path: string; modified: string }>> {
    if (!this.dropbox) throw new Error('Not authenticated with Dropbox');
    
    try {
      const result = await this.dropbox.filesListFolder({ path: '/Budget' });
      const bundles: Array<{ name: string; path: string; modified: string }> = [];
      
      for (const entry of result.result.entries) {
        if (entry['.tag'] === 'folder') {
          // Check if this folder has a ledger.bundle
          try {
            const folderResult = await this.dropbox.filesListFolder({ path: entry.path_lower! });
            const bundleFile = folderResult.result.entries.find(
              file => file.name === 'ledger.bundle'
            );
            
            if (bundleFile && bundleFile['.tag'] === 'file') {
              bundles.push({
                name: entry.name,
                path: entry.path_lower!,
                modified: (bundleFile as any).client_modified
              });
            }
          } catch (error) {
            // Folder might be empty or inaccessible
            continue;
          }
        }
      }
      
      return bundles;
    } catch (error) {
      console.error('Failed to list bundles:', error);
      return [];
    }
  }

  // Utility methods
  private arrayBufferToBase64(buffer: Uint8Array): string {
    const binary = String.fromCharCode.apply(null, Array.from(buffer));
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private async blobToText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(blob);
    });
  }
}

export default DropboxAdapter;